import { Component, Input, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Guid } from 'typescript-guid';
import { IndPageTitleViewDataModel } from '../../../models/indPageTitleDataModel';
import { IndSectionHeaderViewDataModel } from '../../../models/indSectionHeaderDataModel';
import { NavigationService, PageName } from '../../../services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService, ToastStyle } from '../../../services/notification/notification.service';
import { Contact } from '../../../classes/contact/contact.class';
import { SdrFormState, SampleDeliveryRequestService, SdrNotesGate, SdrAllocationExceededError } from '../../../services/sample/sample-delivery-request.service';
import { SampleDeliveryRequest, SdrAllocationDisplay, SdrIoConfigLimit, SdrPickerSku, SdrProductLine, isValidEmail } from '../../../classes/sample/sample-delivery-request.class';
import { SdrLineItemResult } from '../../../data-services/sample/sample-delivery-request.data.service';
import { IONote } from '../../../classes/io/io-note.class';
import { AlertController } from '@ionic/angular';
import { SdrNotesSectionComponent } from '../sdr-notes-section/sdr-notes-section.component';
import { ContactOfflineService } from '../../../services/contact/contact.service';
import { ComponentViewMode, UIService } from '../../../services/ui/ui.service';
import { DeviceService } from '../../../services/device/device.service';
import { IndFormFieldViewDataModel, FormFieldType } from '../../../models/indFormFieldDataModel';
import { Section } from '../../shared/ind-form-list/ind-form-list.component';
import { exceedsOfflineSdrAttachmentBudget } from '../../../data-services/sample/sample-delivery-request.data.service';

interface SdrAddressOption {
  id: string;
  label: string;
  isPrimary: boolean;
}

interface SdrEmailOption {
  id: string;
  label: string;
  isPrimary: boolean;
}

// Numeric dropdown supports up to this many options when the SKU has no maxQty
// configured — IAO uses 10 as the implicit ceiling for the same UI; matching keeps
// the two flows visually consistent.
const QTY_DROPDOWN_DEFAULT_MAX = 10;

@Component({
  selector: 'new-sample-delivery-request',
  templateUrl: 'new-sample-delivery-request.component.html',
  styleUrls: ['new-sample-delivery-request.component.scss'],
})
export class NewSampleDeliveryRequestComponent implements OnInit, OnDestroy {
  @Input() contact: Contact;
  @Input() from: PageName;

  formState: SdrFormState;
  limitsMap: Map<string, SdrIoConfigLimit> = new Map();

  // Customer field — rendered via <ind-form-field> LIST (mirrors IAO).
  // Rebuilt (fresh object reference) on every customer change so the OnPush
  // ind-form-field picks up the new selected chip / source data.
  contactFormField: IndFormFieldViewDataModel;
  // Flat, name-sorted list of selectable contacts, excluding the current
  // customer. Source for the inline filter dropdown.
  contactSourceData: any[] = [];

  // Embedded Notes & Attachments section. Resolves the
  // BU_ACTIVITY_GA_NOTES_ATTACHMENTS config internally; read here only to gate
  // submit when the config marks notes/attachments mandatory.
  @ViewChild(SdrNotesSectionComponent) private notesSection?: SdrNotesSectionComponent;

  detailsHeader: IndSectionHeaderViewDataModel;

  addressOptions: SdrAddressOption[] = [];
  emailOptions: SdrEmailOption[] = [];
  isAddressDropdownOpen = false;
  isEmailDropdownOpen = false;

  isPickerOpen = false;
  pickerSearchText = '';

  /** Own re-entrancy guard for a fast double-tap on Submit (set synchronously,
   *  before any await — mirrors the inline meeting form's local flag). */
  isSubmitting = false;

  /**
   * This draft's own client-generated SDR id, minted once per component
   * instance (a fresh form = a fresh id). Passed into submit()/submitOffline()/
   * reconcileSubmit() so the service can track this attempt's in-flight/pending-
   * reconcile state without it being confused with — or clobbered by — any
   * other SDR form (e.g. the inline meeting form) submitting concurrently.
   */
  private readonly sdrAttemptId = Guid.create().toString();

  /**
   * Per-section touched flags — mirror `ind-form-field`'s `_isClicked` pattern.
   * A section's invalid chrome (red separator / red search-bar border + "This
   * is a required field" helper text) renders only when that section is both
   * touched AND empty. Auto-cleared sections (address / email after a customer
   * change to a contact without primaries) stay neutral until the user
   * themselves engages with them — keeps the form from screaming red the
   * moment a customer is swapped. See spec §11.10.
   */
  touched = {
    contact: false,
    deliveryAddress: false,
    email: false,
    products: false,
  };

  private _qtyDropdownEl: HTMLElement | null = null;
  private _qtyDropdownCleanup: (() => void) | null = null;

  get filteredPickerSkus(): SdrPickerSku[] {
    // OMNI-61531: only SKUs allocated to the current contact populate the
    // picker — this.sdrSkus (the raw device-wide catalog) is no longer used
    // directly here.
    const eligibleSkus = this.sdrService.getEligibleSkusForContact(this.formState?.contactId);
    const search = this.pickerSearchText.toLowerCase();
    return search
      ? eligibleSkus.filter((s) => s.skuName?.toLowerCase().includes(search))
      : eligibleSkus;
  }

  get isSubmitEnabled(): boolean {
    return this.sdrService.isFormValid(this.formState, this.notesGate)
      && !this.isSubmitting
      && !this.sdrService.isSubmittingId(this.sdrAttemptId);
  }

  /**
   * Submit-time notes/attachments requirement, sourced from the embedded notes
   * section. Notes and attachments are gated independently (the 8 cases). Before
   * the view inits the gate is all-false → never blocks; once the section
   * exists, each requirement is satisfied by content present anywhere (existing
   * notes, staged notes, or the live composer).
   */
  private get notesGate(): SdrNotesGate {
    const section = this.notesSection;
    if (!section) {
      return { notesMandatory: false, notesSatisfied: true, attachmentsMandatory: false, attachmentsSatisfied: true };
    }
    return {
      notesMandatory: section.notesMandatory,
      notesSatisfied: section.hasText,
      attachmentsMandatory: section.attachmentsMandatory,
      attachmentsSatisfied: section.hasAttachment,
    };
  }

  // Per-section invalid flags — true only when the user has touched the
  // section AND it is currently empty. Drives the `--invalid` modifier on
  // each section's row + the "This is a required field" / products-empty
  // helper line. See spec §11.10.
  get isDeliveryAddressInvalid(): boolean {
    return this.touched.deliveryAddress && !this.sdrService.getFieldValidity(this.formState).deliveryAddress;
  }

  get isEmailInvalid(): boolean {
    return this.touched.email && !this.sdrService.getFieldValidity(this.formState).email;
  }

  /** OMNI-60495: hide the mandatory-field icon when the BU has turned off the email requirement. */
  get isEmailMandatory(): boolean {
    return this.sdrService.isEmailMandatory;
  }

  get isProductsInvalid(): boolean {
    return this.touched.products && !this.sdrService.getFieldValidity(this.formState).products;
  }

  /**
   * Recomputed every change-detection pass — `ind-page-title` is OnPush, so a
   * fresh object reference here is how it picks up state changes (e.g. the
   * Submit control transitioning between enabled and disabled). Left control
   * uses an Ionicon name (`chevron-back-outline`) — same pattern as
   * `new-sample-activity` and every other pushed create page. The SVG asset
   * `back_to_home_btn.svg` is reserved for list pages and renders as a home
   * icon, which is why the earlier draft of this component showed the wrong
   * glyph.
   */
  get pageTitleData(): IndPageTitleViewDataModel {
    return {
      id: 'new-sdr-page-title',
      title: this.translate.instant('NEW_SAMPLE_DELIVERY_REQUEST'),
      controls: [
        {
          id: 'close',
          icon: 'chevron-back-outline',
          isDisabled: false,
          align: 'left',
        },
        {
          id: 'sdr-submit',
          imgSrc: 'assets/imgs/header_complete.svg',
          name: this.translate.instant('SUBMIT'),
          isDisabled: !this.isSubmitEnabled,
          align: 'right',
        },
      ],
    };
  }

  get isDeliveryAddressPrimary(): boolean {
    return !!this.addressOptions.find((a) => a.id === this.formState?.deliveryAddressId)?.isPrimary;
  }

  get isEmailPrimary(): boolean {
    return !!this.emailOptions.find((e) => e.id === this.formState?.emailId)?.isPrimary;
  }

  constructor(
    private readonly navService: NavigationService,
    private readonly translate: TranslateService,
    private readonly notificationService: NotificationService,
    public readonly sdrService: SampleDeliveryRequestService,
    private readonly alertCtrl: AlertController,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly contactService: ContactOfflineService,
    private readonly uiService: UIService,
    private readonly device: DeviceService,
  ) {}

  ngOnInit() {
    this.limitsMap = this.sdrService.getProductLimitsMap();
    this.rebuildOptionsFromContact();

    this.detailsHeader = {
      id: 'sdr-details-header',
      title: this.translate.instant('DETAILS'),
      controls: [],
    };

    const primaryAddress = this.contact?.primaryAddress;
    const primaryEmail = this.contact?.emailAddressList?.find((e) => e.isPrimary);
    this.formState = this.sdrService.initFormState(
      this.contact?.ID ?? '',
      this.contact?.fullName ?? '',
      primaryAddress?.addressId ?? '',
      primaryAddress?.compositeAdd ?? '',
      primaryEmail?.emailAddressId ?? '',
      primaryEmail?.emailAddress ?? '',
    );

    // Contact with no saved address/email starts in free-text mode (rebuildOptions
    // ran before formState existed, so apply the same defaulting here).
    if (this.addressOptions.length === 0) this.formState.useAlternateAddress = true;
    if (this.emailOptions.length === 0) this.formState.useAlternateEmail = true;

    this._buildContactFormField();

    // Fire-and-forget: refresh the Manual-Distribution allocation cache for
    // this contact (limit-validation pre-check, OMNI-59706). Goes through
    // _refreshAllocationsForCurrentContact (not a bare sync call) so that if
    // the rep adds a product in the split-second before this resolves, that
    // line still gets its allocation info applied once the fetch completes,
    // instead of staying unchecked for the rest of the session — addSkuToForm
    // alone only reads whatever's cached at the moment it's called.
    void this._refreshAllocationsForCurrentContact();
  }

  ngOnDestroy() {
    this._destroyQtyDropdown();
  }

  // ──────────────── Customer ────────────────

  clearCustomer() {
    if (!this.formState) return;
    this.formState.contactId = '';
    this.formState.contactName = '';
    this.formState.deliveryAddressId = '';
    this.formState.deliveryAddressLabel = '';
    this.formState.emailId = '';
    this.formState.emailLabel = '';
    this.addressOptions = [];
    this.emailOptions = [];
    this.isAddressDropdownOpen = false;
    this.isEmailDropdownOpen = false;
    this._buildContactFormField();
  }

  /**
   * Pushes the full Contact List on top of the SDR form (master-level push) so
   * the user can pick a different HCP. Mirrors the CustomerInsight + Event
   * single-contact-replace pattern — see spec §4.1.1.
   *
   * Contract:
   *   - `selectedContactId` in navParams tells contact-list which row to pre-
   *     mark as radio-selected (so the "Selected" header at the top of the
   *     picker shows the current SDR customer). Read by the SDR case in the
   *     SELECTION switch at `contact-list.ts:~2552`.
   *   - `callbackEvent` is invoked by the SDR branch in `onCloseModal` at
   *     `contact-list.ts:~4115` with `[radioSelectedContact]` on done. The same
   *     branch resets `contactPageMode` + `accessedContactListFrom` and restores
   *     `holdContactInformation` on both done and cancel — so the parent
   *     contact-tool returns to a clean LISTVIEW state when the picker pops.
   */
  openContactList() {
    this.contactService.contactPageMode = ComponentViewMode.SELECTION;
    this.contactService.accessedContactListFrom = PageName.NewSampleDeliveryRequestComponent;
    const callbackEvent = (data: any) => {
      if (Array.isArray(data) && data.length > 0) {
        const contact = data[0] as Contact;
        if (contact?.ID && contact.ID !== this.formState?.contactId) {
          this.applyContactChange(contact);
        }
      }
    };
    this.navService.pushWithPageTracking(
      PageName.ContactPageComponent,
      PageName.ContactPageComponent,
      { callbackEvent, selectedContactId: this.formState?.contactId ?? null },
      PageName.ContactPageComponent,
    );
  }

  /**
   * Builds the ind-form-field viewData for the Customer LIST field. Must be
   * re-invoked (producing a fresh object) whenever the selected customer
   * changes — ind-form-field is OnPush and keys off input-reference identity.
   *
   * endIcons: searchblue = inline filter (handled inside ind-form-field),
   *           expandblue = escape to the full Contact List (openContactList).
   */
  private _buildContactFormField() {
    const hasContact = !!this.formState?.contactId;

    this._buildContactSourceData();
    const selectedChipData = this._getSelectedContactData();

    // No sentinel row when empty. A placeholder "gate" item would make
    // ind-form-field.shouldShowDropDown() truthy on focus (selectedData.length
    // > 0), rendering an empty bordered dropdown box (the stray "grey bar").
    // With [] the dropdown stays closed until the user actually types.
    const selectedData = hasContact ? selectedChipData : [];

    this.contactFormField = {
      label: this.translate.instant('CUSTOMER'),
      inputText: '',
      customPlaceholderLabel: hasContact ? '' : this.translate.instant('SELECT_CUSTOMER'),
      id: 'sdr-contact-field',
      isReadOnly: true,
      showArrow: false,
      isRequired: !hasContact,
      // Drives ind-form-field's "This is a required field" helper text under
      // the LIST field (in addition to the required asterisk) once the
      // customer is cleared. Gated on touched.contact so the error only
      // shows once the rep has actually opened the picker and left it
      // empty — mirrors Delivery Address / Email / Products, and the inline
      // SDR form's identical field — instead of showing red on first render.
      isEmptyRequiredField: this.touched.contact && !hasContact,
      errorMessage: this.translate.instant('THIS_IS_A_REQUIRED_FIELD'),
      formFieldType: FormFieldType.LIST,
      isDisabled: false,
      isSearchKeyRetain: true,
      hideInlineChip: !hasContact,
      modalConfiguration: {
        searchTitle: this.translate.instant('SEARCH_CONTACTS'),
        selectedTitle: this.translate.instant('SELECTED_CONTACTS'),
        titleEndIcon: 'expandblue',
        searchInputConfig: {
          label: this.translate.instant('CONTACTS'),
          placeholder: this.translate.instant('TYPE') + '...',
        },
        showSearch: true,
      },
      filterMethod: this._filterContactData.bind(this),
      endIcons: [
        { iconName: 'searchblue', overRideDisabledProperty: false },
        { iconName: 'expandblue', overRideDisabledProperty: true },
      ],
      sections: this._generateContactSections(hasContact ? selectedChipData : []),
      selectedData,
      eventHandler: (id: string, event: any, eventName: string, data: any) => {
        if (data && eventName === 'item-click') {
          const contact = data.refObject as Contact;
          if (contact?.ID && contact.ID !== this.formState?.contactId) {
            this.applyContactChange(contact);
          }
        } else if (eventName === 'closebutton') {
          if (this.formState?.contactId) {
            this.clearCustomer();
          }
        }
      },
    };
  }

  /**
   * Flat source list for the inline filter dropdown: all active contacts
   * except the currently selected one, mapped to ind-form-list item shape and
   * sorted by display name.
   */
  private _buildContactSourceData() {
    const selectedId = this.formState?.contactId || '';
    this.contactSourceData = (this.contactService.contacts || [])
      .filter((c) => c.isActive && c.ID !== selectedId)
      .map((contact) => ({
        id: contact.ID,
        title: this._contactDisplayName(contact),
        image: '',
        refObject: contact,
      }))
      .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }

  /** Selected-chip data for the "Selected" section + inline chip. */
  private _getSelectedContactData(): any[] {
    if (!this.formState?.contactId) return [];
    return [{
      id: this.formState.contactId,
      title: this.formState.contactName || '',
      image: '',
      refObject: this.contact || null,
      endIcon: 'closebutton',
    }];
  }

  /** Word-wise case-insensitive contains match on the contact display name. */
  private _filterContactData(query: string): any[] {
    const queryWords = query.toLowerCase().split(' ').filter((w) => w.trim() !== '');
    const selectedId = this.formState?.contactId || '';
    return this.contactSourceData
      .filter((item) => {
        const title = item.title.toLowerCase();
        return queryWords.every((word) => title.includes(word));
      })
      .filter((item) => item.id !== selectedId);
  }

  /** Selected + Results sections. No meeting/near-me data — SDR is not a meeting. */
  private _generateContactSections(selectedSectionData: any[]): Section[] {
    return [
      {
        id: 'selected',
        title: this.translate.instant('SELECTED'),
        showCountOnTitle: true,
        maxResultsView: 5,
        bottomButtonShow: false,
        expand: false,
        showBottomButton: false,
        bottomButtonConfig: {},
        data: selectedSectionData,
      },
      {
        id: 'results',
        title: this.translate.instant('AL_RESULTS'),
        showFilteredData: true,
        showCountOnTitle: true,
        maxResultsView: 5,
        bottomButtonShow: true,
        expand: true,
        showBottomButton: true,
        bottomButtonConfig: {
          exceedMaxResultButton: {
            id: 'view-all',
            label: this.translate.instant('VIEW_MORE'),
          },
          lessThanResultButton: {
            id: 'show-other',
            label: this.translate.instant('SEARCH_OTHER_WITH_TEXT', { text: this.translate.instant('CUSTOMER') }),
          },
        },
        data: [],
      },
    ];
  }

  /** Display name resolver — mirrors IAO's _contactDisplayName. */
  private _contactDisplayName(contact: Contact): string {
    return contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
  }

  /**
   * Routes the ind-form-field LIST "additional controls" to the existing full
   * Contact List escape hatch:
   *   - expandblue  → header expand icon
   *   - view-all    → Results "View More" button
   *   - show-other  → Results "Search other Customer" button
   * All three open the four-chain contact-list picker (spec §4.1.1).
   */
  onContactFormAdditionalControl(ev: any) {
    const id = typeof ev === 'string' ? ev : ev?.id;
    if (id === 'expandblue' || id === 'view-all' || id === 'show-other') {
      this.openContactList();
    }
  }

  /** Fires when the rep taps the (read-only) Customer field to open the
   *  picker — mirrors the inline SDR form's identical handler, marking the
   *  section touched so the required-field error only shows once the rep
   *  has actually engaged it, not on first render. */
  onCustomerFieldClicked(): void {
    if (this.touched.contact) return;
    this.touched.contact = true;
    this._buildContactFormField();
  }

  private applyContactChange(contact: Contact) {
    this.contact = contact;
    this.rebuildOptionsFromContact();
    const primary = contact.primaryAddress;
    const primaryEmail = contact.emailAddressList?.find((e) => e.isPrimary);
    this.formState.contactId = contact.ID;
    this.formState.contactName = contact.fullName ?? '';
    this.formState.deliveryAddressId = primary?.addressId ?? '';
    this.formState.deliveryAddressLabel = primary?.compositeAdd ?? '';
    this.formState.emailId = primaryEmail?.emailAddressId ?? '';
    this.formState.emailLabel = primaryEmail?.emailAddress ?? '';
    this._buildContactFormField();

    // Swapping the customer means any already-added product lines' allocation
    // info (resolved against the PREVIOUS contact) is now stale — re-sync for
    // the new contact and re-resolve it. Fire-and-forget: until this
    // resolves, existing lines keep their old allocationRemaining, which is a
    // stale-but-safe optimistic value (Dynamics validates authoritatively at
    // submit time regardless).
    void this._refreshAllocationsForCurrentContact();
  }

  /** Re-syncs the Manual-Distribution allocation cache for the form's current
   *  contact and re-resolves allocationRemaining/allocationUnlimited/
   *  allocationNotEligible on every already-added product line against the
   *  refreshed cache — so a customer swap can't leave a line's eligibility
   *  status stale relative to the newly-selected contact. */
  private async _refreshAllocationsForCurrentContact(forceRefresh = false): Promise<void> {
    const contactId = this.formState?.contactId;
    if (!contactId) return;
    await this.sdrService.syncManualAllocationsForContact(contactId, forceRefresh);
    for (const product of this.formState?.products ?? []) {
      this.sdrService.applyAllocationDisplay(product, this._resolveAllocationDisplay(contactId, product.skuId));
    }
  }

  /**
   * Resolves the three allocation-derived display fields for one SKU.
   * Deliberately blanks remaining/unlimited whenever isAllocationBlocking is
   * true (expired config, ineligible customer, or no config at all):
   * `_toSkuMap` can still hand back an outdated row's `remaining` when no
   * currently-usable row exists for this SKU (e.g. only an expired config on
   * file) — showing that stale "Remaining: N" chip next to the "not
   * currently available" warning would flatly contradict it. Confirmed live:
   * an expired config for Wayne Davis/Sample-Allocation-Sku still showed
   * "Remaining: 4" alongside the blocking message.
   */
  private _resolveAllocationDisplay(contactId: string, skuId: string): SdrAllocationDisplay {
    return this.sdrService.resolveAllocationDisplay(contactId, skuId);
  }

  // ──────────────── Address / Email dropdowns ────────────────

  private rebuildOptionsFromContact() {
    this.addressOptions = (this.contact?.addressesList ?? []).map((a) => ({
      id: a.addressId,
      label: a.compositeAdd ?? '',
      isPrimary: !!a.isPrimary,
    }));
    this.emailOptions = (this.contact?.emailAddressList ?? [])
      .filter((e) => !e.statecode || e.statecode !== 1)
      .map((e) => ({
        id: e.emailAddressId,
        label: e.emailAddress ?? '',
        isPrimary: !!e.isPrimary,
      }));
    // No saved value at all ⇒ show the free-text field directly (spec §5.1).
    // Bidirectional so a customer SWAP resets cleanly: a contact with saved
    // values returns to saved mode (and any stale free-text is discarded),
    // a contact without them drops into free-text. Guarded because this method
    // also runs in ngOnInit BEFORE formState exists; ngOnInit re-applies the
    // same defaulting explicitly after initFormState.
    if (this.formState) {
      this.formState.useAlternateAddress = this.addressOptions.length === 0;
      if (!this.formState.useAlternateAddress) this.formState.alternateDeliveryAddress = '';
      this.formState.useAlternateEmail = this.emailOptions.length === 0;
      if (!this.formState.useAlternateEmail) this.formState.alternateEmail = '';
    }
  }

  toggleAddressDropdown() {
    this.isAddressDropdownOpen = !this.isAddressDropdownOpen;
    this.isEmailDropdownOpen = false;
    this.closePicker();
    this.touched.deliveryAddress = true;
  }

  toggleEmailDropdown() {
    this.isEmailDropdownOpen = !this.isEmailDropdownOpen;
    this.isAddressDropdownOpen = false;
    this.closePicker();
    this.touched.email = true;
  }

  selectAddress(addr: SdrAddressOption) {
    this.formState.deliveryAddressId = addr.id;
    this.formState.deliveryAddressLabel = addr.label;
    this.isAddressDropdownOpen = false;
  }

  selectEmail(opt: SdrEmailOption) {
    this.formState.emailId = opt.id;
    this.formState.emailLabel = opt.label;
    this.isEmailDropdownOpen = false;
  }

  trackByOptionId(_: number, item: { id: string }): string {
    return item.id;
  }

  // ──────────────── Alternate (free-text) address / email ────────────────

  get isAlternateAddressActive(): boolean {
    return !!this.formState?.useAlternateAddress;
  }
  get isAlternateEmailActive(): boolean {
    return !!this.formState?.useAlternateEmail;
  }
  get hasSavedAddresses(): boolean {
    return this.addressOptions.length > 0;
  }
  get hasSavedEmails(): boolean {
    return this.emailOptions.length > 0;
  }

  /**
   * Switch the address field into free-text mode. Deliberately does NOT mark the
   * section touched — entering the field shouldn't fire a "required" error before
   * the user has had a chance to type. The error surfaces on input (type-then-
   * clear) or on blur (clicked in and left empty) instead.
   */
  enableAlternateAddress() {
    this.formState.useAlternateAddress = true;
    this.formState.deliveryAddressId = '';
    this.formState.deliveryAddressLabel = '';
    this.isAddressDropdownOpen = false;
    // Reset touched: opening the dropdown to reach this option set it true, but
    // a freshly-revealed empty field shouldn't show "required" until the user
    // engages it (types-then-clears, or blurs).
    this.touched.deliveryAddress = false;
  }

  /** Back to the saved-address dropdown. */
  useSavedAddress() {
    this.formState.useAlternateAddress = false;
    this.formState.alternateDeliveryAddress = '';
    this.touched.deliveryAddress = false;
    if (this.addressOptions.length === 1) {
      this.selectAddress(this.addressOptions[0]);
    } else {
      this.isAddressDropdownOpen = true;
    }
  }

  onAlternateAddressInput(ev: Event) {
    this.formState.alternateDeliveryAddress = (ev.target as HTMLInputElement).value ?? '';
    // The user has engaged the field — from now on an empty value is an error.
    this.touched.deliveryAddress = true;
  }

  /** Engaging then leaving the field empty is a touch — surface the required error. */
  onAlternateAddressBlur() {
    this.touched.deliveryAddress = true;
  }

  enableAlternateEmail() {
    this.formState.useAlternateEmail = true;
    this.formState.emailId = '';
    this.formState.emailLabel = '';
    this.isEmailDropdownOpen = false;
    this.touched.email = false;
  }

  useSavedEmail() {
    this.formState.useAlternateEmail = false;
    this.formState.alternateEmail = '';
    this.touched.email = false;
    if (this.emailOptions.length === 1) {
      this.selectEmail(this.emailOptions[0]);
    } else {
      this.isEmailDropdownOpen = true;
    }
  }

  onAlternateEmailInput(ev: Event) {
    this.formState.alternateEmail = (ev.target as HTMLInputElement).value ?? '';
    this.touched.email = true;
  }

  onAlternateEmailBlur() {
    this.touched.email = true;
  }

  /** True once the user has typed an invalid alternate email (touched + non-empty + malformed). */
  get isAlternateEmailInvalid(): boolean {
    const v = this.formState?.alternateEmail?.trim();
    return this.isAlternateEmailActive && this.touched.email && !!v && !isValidEmail(v);
  }

  // ──────────────── Picker ────────────────

  openPicker() {
    this.isPickerOpen = true;
    this.isAddressDropdownOpen = false;
    this.isEmailDropdownOpen = false;
    this.touched.products = true;
  }

  closePicker() {
    this.isPickerOpen = false;
    this.pickerSearchText = '';
  }

  onPickerInput() {
    this.openPicker();
  }

  /**
   * Sample-Drop pattern: clicking a row in the picker adds the SKU with its
   * default qty (configured min, or 1 if unconfigured). The user then adjusts
   * qty / removes from the selected products grid below.
   */
  onPickerRowClick(sku: SdrPickerSku) {
    if (this.isSkuInForm(sku.skuId)) return;
    this.addSkuToForm(sku);
  }

  isSkuInForm(skuId: string): boolean {
    return !!this.formState?.products?.some((p) => p.skuId === skuId);
  }

  // Limit lookups lower-case the SKU id: the iO config GUIDs are stored
  // lower-cased (getIOConfigurationValue lower-cases TEXT values), so matching
  // raw `sku.ID` casing directly would miss every configured limit.
  hasSkuLimit(skuId: string): boolean {
    return this.limitsMap.has(skuId?.toLowerCase());
  }

  getSkuLimitLabel(skuId: string): string {
    const entry = this.limitsMap.get(skuId?.toLowerCase());
    if (!entry) return '';
    const hasMin = entry.min != null;
    const hasMax = entry.max != null;
    if (hasMin && hasMax) return this.translate.instant('SDR_PRODUCT_LIMIT', { min: entry.min, max: entry.max });
    if (hasMin) return this.translate.instant('SDR_PRODUCT_LIMIT_MIN', { min: entry.min });
    if (hasMax) return this.translate.instant('SDR_PRODUCT_LIMIT_MAX', { max: entry.max });
    return '';
  }

  getSkuMinQty(skuId: string): number {
    return this.limitsMap.get(skuId?.toLowerCase())?.min ?? 1;
  }

  getSkuMaxQty(skuId: string): number | null {
    return this.limitsMap.get(skuId?.toLowerCase())?.max ?? null;
  }

  async addSkuToForm(sku: SdrPickerSku): Promise<void> {
    if (this.isSkuInForm(sku.skuId)) return;

    // Online-only, TTL-guarded refresh of this contact's allocation config
    // right before checking it — syncManualAllocationsForContact itself
    // no-ops offline (falls back to the last disk snapshot) and skips the
    // network call if this contact was already fetched within the last 3
    // minutes, so this is cheap for successive adds and never blocks offline.
    await this.sdrService.syncManualAllocationsForContact(this.formState.contactId);

    const minQty = this.getSkuMinQty(sku.skuId);
    const maxQty = this.getSkuMaxQty(sku.skuId);
    // Best-effort optimistic pre-check (OMNI-59706): notEligible is null-safe
    // when no config exists at all for this SKU, or the cache isn't
    // populated for this contact yet (sync hasn't completed/failed/offline
    // with no snapshot) — see _resolveAllocationDisplay/isAllocationBlocking.
    const display = this._resolveAllocationDisplay(this.formState.contactId, sku.skuId);
    const qty = Math.max(minQty, 1);

    this.formState.products.push({
      skuId: sku.skuId,
      skuName: sku.skuName,
      productId: sku.brandId,
      productName: sku.brandName,
      qty,
      minQty,
      maxQty,
      hasLimit: this.hasSkuLimit(sku.skuId),
      allocationRemaining: display.remaining,
      allocationUnlimited: display.unlimited,
      allocationNotEligible: display.notEligible,
      allocationEligible: display.eligible,
      allocationStart: display.start,
      allocationEnd: display.end,
    });

    this.closePicker();
  }

  /** True when a Manual-Distribution Customer Allocation record was found for
   *  this SKU (i.e. there's a remaining balance worth showing as a chip). */
  hasAllocationInfo(product: SdrProductLine): boolean {
    return !product.allocationUnlimited && product.allocationRemaining != null;
  }

  getAllocationLabel(product: SdrProductLine): string {
    if (!this.hasAllocationInfo(product)) return '';
    return this.translate.instant('SDR_ALLOCATION_REMAINING', { remaining: product.allocationRemaining });
  }

  hasAllocationDetails(product: SdrProductLine): boolean {
    return this.sdrService.hasAllocationDetails(product);
  }

  getAllocationValidity(product: SdrProductLine): string {
    return this.sdrService.formatAllocationValidity(product);
  }

  // ──────────────── Grid ────────────────

  removeProduct(index: number) {
    this.formState.products.splice(index, 1);
    // Removing a product is a direct touch of the products section; if the
    // user just emptied it, the red "Add at least one product" hint appears.
    this.touched.products = true;
  }

  /**
   * Resolves the qty error translation key for a product line, or null when
   * the qty is acceptable. Mirrors Sample-Drop (IAO) inline error chrome:
   *   - 'notEligible' → this SKU's Manual-Distribution config is confirmed
   *     inapplicable to this customer right now (expired/not-yet-started, or
   *     not associated with no prior history) — independent of quantity
   *   - 'range' → typed value falls outside the configured min/max band
   *   - 'zero'  → 0 / blank, which can never be submitted
   */
  getQtyErrorKey(product: SdrProductLine): string | null {
    switch (this.sdrService.getProductQtyError(product)) {
      case 'notEligible': return 'SDR_CUSTOMER_NOT_ELIGIBLE';
      case 'range': return 'SDR_QTY_OUTSIDE_RANGE';
      case 'zero': return 'SDR_QTY_SET_HINT';
      case 'allocation': return 'SDR_QTY_EXCEEDS_ALLOCATION';
      default: return null;
    }
  }

  /**
   * Stepper bounds. The − button now honours the configured minimum the same
   * way the + button honours the maximum: when a min/max iO config is present
   * (maxQty != null) the floor is minQty; otherwise the line may decrement to
   * 0 to flag it for removal (submit blocks any qty-0 line via §11.6).
   */
  isDecrementDisabled(product: SdrProductLine): boolean {
    const floor = product.hasLimit ? product.minQty : 0;
    return product.qty <= floor;
  }

  isIncrementDisabled(product: SdrProductLine): boolean {
    return product.maxQty !== null && product.qty >= product.maxQty;
  }

  /**
   * Stepper step. Bounds are delegated to the same predicates that drive the
   * button disabled-state — so a value typed outside the configured band (the
   * only way to land out of band) can still be stepped back toward it: the
   * far-side button is disabled, the near-side one pulls the qty in.
   */
  updateQty(index: number, delta: number) {
    const product = this.formState.products[index];
    if (delta < 0 && this.isDecrementDisabled(product)) return;
    if (delta > 0 && this.isIncrementDisabled(product)) return;
    product.qty = product.qty + delta;
  }

  /**
   * Manual entry keeps whatever the user types (floored, non-negative) rather
   * than silently clamping — an out-of-band value surfaces the inline
   * "outside configured range" error (Sample-Drop pattern) and blocks submit,
   * letting the user see and correct their input.
   */
  onGridQtyInput(index: number, ev: Event) {
    const product = this.formState.products[index];
    const raw = Number((ev.target as HTMLInputElement).value);
    if (Number.isNaN(raw)) return;
    product.qty = Math.max(0, Math.floor(raw));
  }

  // ──────────────── Qty numeric dropdown (mirrors IAO openQuantityDropdown) ────────────────

  openQtyDropdown(event: Event, productIndex: number) {
    event.stopPropagation();
    // Toggle: close if already open
    if (this._qtyDropdownEl) {
      this._destroyQtyDropdown();
      return;
    }
    const product = this.formState.products[productIndex];
    if (!product) return;

    const btn = (event.target as HTMLElement).closest('.sdr-qty-dropdown-btn') as HTMLElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const min = product.hasLimit ? Math.max(1, product.minQty) : 1;
    // When no cap is configured, show QTY_DROPDOWN_DEFAULT_MAX options starting
    // from the floor (min). For min=100 this gives 100–109; for unconfigured
    // lines it gives 1–10. A configured maxQty always wins as the ceiling.
    const max = product.maxQty !== null
      ? product.maxQty
      : product.hasLimit
        ? product.minQty + QTY_DROPDOWN_DEFAULT_MAX - 1
        : QTY_DROPDOWN_DEFAULT_MAX;
    if (max < min) return;

    this._qtyDropdownEl = this.renderer.createElement('div');
    this.renderer.appendChild(document.body, this._qtyDropdownEl);

    const s = this._qtyDropdownEl.style;
    s.position = 'fixed';
    s.width = '56px';
    s.left = `${rect.left - 18}px`;

    const dropdownMaxH = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    if (spaceBelow < dropdownMaxH && rect.top > spaceBelow) {
      s.bottom = `${window.innerHeight - rect.top + 4}px`;
    } else {
      s.top = `${rect.bottom + 4}px`;
    }
    s.zIndex = '99999';
    s.background = '#ffffff';
    s.border = '1px solid #e5e5ea';
    s.borderRadius = '4px';
    s.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
    s.maxHeight = `${dropdownMaxH}px`;
    s.overflowY = 'auto';
    (s as any)['-webkit-overflow-scrolling'] = 'touch';

    let html = '';
    for (let i = min; i <= max; i++) {
      const isActive = i === product.qty;
      html += `<div class="sdr-qty-dd-option" data-value="${i}" style="padding:6px 0;text-align:center;font-size:14px;font-weight:${isActive ? '600' : '400'};color:${isActive ? '#007aff' : '#1a1a1a'};background:${isActive ? 'rgba(0,122,255,0.08)' : 'transparent'};cursor:pointer;">${i}</div>`;
    }
    this._qtyDropdownEl.innerHTML = html;

    const activeOpt = this._qtyDropdownEl.querySelector(`[data-value="${product.qty}"]`) as HTMLElement;
    if (activeOpt) activeOpt.scrollIntoView({ block: 'center' });

    const options = this._qtyDropdownEl.querySelectorAll('.sdr-qty-dd-option');
    options.forEach((el: HTMLElement) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const val = parseInt(el.getAttribute('data-value') ?? '', 10);
        if (!Number.isNaN(val)) {
          this.ngZone.run(() => {
            product.qty = val;
            this._destroyQtyDropdown();
          });
        }
      });
      el.addEventListener('mouseover', () => {
        if (parseInt(el.getAttribute('data-value') ?? '', 10) !== product.qty) {
          el.style.background = '#f2f2f7';
        }
      });
      el.addEventListener('mouseout', () => {
        if (parseInt(el.getAttribute('data-value') ?? '', 10) !== product.qty) {
          el.style.background = 'transparent';
        }
      });
    });

    const outsideHandler = (e: MouseEvent) => {
      if (this._qtyDropdownEl && !this._qtyDropdownEl.contains(e.target as Node)) {
        this._destroyQtyDropdown();
      }
    };
    setTimeout(() => document.addEventListener('mousedown', outsideHandler));
    this._qtyDropdownCleanup = () => document.removeEventListener('mousedown', outsideHandler);
  }

  private _destroyQtyDropdown() {
    if (this._qtyDropdownEl) {
      this._qtyDropdownEl.remove();
      this._qtyDropdownEl = null;
    }
    if (this._qtyDropdownCleanup) {
      this._qtyDropdownCleanup();
      this._qtyDropdownCleanup = null;
    }
  }

  trackById(_: number, item: SdrPickerSku): string {
    return item.skuId;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ──────────────── Submit / discard ────────────────

  /**
   * `ind-page-title` emits the clicked control's id as a raw string (see
   * `IndPageTitleComponent.onButtonClick` — `this.onControlClick.emit(button.id)`).
   * Earlier draft typed this parameter as `{ id: string }` and compared
   * `event.id`, which was always undefined — both Submit and Back were
   * silently no-ops as a result. Always destructure the string, not an object.
   */
  onPageTitleControlClick(id: string) {
    if (id === 'sdr-submit') {
      this.onSubmit();
    } else if (id === 'close') {
      this.onDiscard();
    }
  }

  async onSubmit() {
    // Re-entrancy guard: closes the change-detection-timing window where two
    // near-simultaneous taps both reach onSubmit before the page-title control
    // re-renders disabled.
    if (this.isSubmitting) return;

    // Claim the guard synchronously (before the first await) so it covers the
    // whole flow, reconcile included. Set only inside submit() before, the
    // reconcile path was unguarded: a fast double-tap could run two reconciles
    // that both confirm absence and both POST, duplicating the SDR. The finally
    // releases it on every exit; submit() still sets its own id-scoped flag too
    // — the overlap is safe.
    this.isSubmitting = true;
    try {
      // A previous submit failed but may have committed server-side (network drop
      // on the response leg). Before re-posting — which would duplicate the record,
      // since a fresh submit mints a NEW client id — verify by the remembered id.
      // If the record is there, the earlier submit actually succeeded: navigate to
      // it instead of posting again. Runs before validation: an already-created
      // record is authoritative regardless of any edits made to this form since.
      // Reconcile needs the server, so it's skipped while offline — an offline
      // submit uses submitOffline()'s own locally-generated id, nothing to
      // reconcile against.
      if (!this.device.isOffline && this.sdrService.hasPendingReconcile(this.sdrAttemptId)) {
        if (await this.reconcileBeforeSubmit()) return;
      }

      const reason = this.sdrService.validateForSubmit(this.formState, this.notesGate);
      if (reason === 'requiredFields') {
        this.toast('SDR_VALIDATION_REQUIRED_FIELDS', ToastStyle.DANGER);
        return;
      }
      if (reason === 'invalidEmail') {
        this.toast('SDR_INVALID_EMAIL', ToastStyle.DANGER);
        return;
      }
      if (reason === 'notes') {
        this.toast(this.notesSection?.notesRequiredMessageKey ?? 'SDR_NOTE_REQUIRED', ToastStyle.DANGER);
        return;
      }
      if (reason === 'productQty') {
        this.toast('SDR_VALIDATION_PRODUCT_QTY', ToastStyle.DANGER);
        return;
      }

      // Pull anything the user staged in the notes section (saved-this-session
      // notes + the in-composer draft if non-empty) so it rides the same batch
      // as the SDR create — the data service binds annotations to the
      // pre-generated SDR id atomically.
      const stagedNotes = this.notesSection?.getStagedNotes() ?? [];

      if (this.device.isOffline) {
        // Bound the cumulative attachment size so a large draft fails fast with
        // a clear message rather than risking a PouchDB write failure on a
        // low-end device (mirrors the in-meeting inline SDR editor, OMNI-61002).
        if (exceedsOfflineSdrAttachmentBudget(stagedNotes)) {
          this.toast('SDR_OFFLINE_ATTACHMENT_TOO_LARGE', ToastStyle.DANGER);
          return;
        }
        try {
          const { sdr, notes } = await this.sdrService.submitOffline(this.formState, stagedNotes, undefined, this.sdrAttemptId);
          this.toast('SDR_SAVED_OFFLINE_WILL_SYNC', ToastStyle.INFO);
          this.navigateToDetail(sdr, notes);
        } catch (err) {
          console.error('SDR offline submit failed', err);
          if (err instanceof SdrAllocationExceededError) {
            this._notifyAllocationExceeded(err);
          } else {
            this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
          }
        }
        return;
      }

      // Buffering overlay shown only after validation passes, so validation
      // toasts still fire instantly. `bypassDuration: true` suppresses the
      // loader's default 15s auto-dismiss — on a slow network the submit can take
      // longer than 15s, and a self-dismissing spinner left the user staring at a
      // greyed-out form with no spinner and no toast until the call finally
      // settled (Issue 3). The loader now stays up until submit actually
      // completes; the batch timeout (SDR_SUBMIT_TIMEOUT_MS) guarantees it always
      // settles, and the finally below dismisses it on success, error, or timeout.
      await this.uiService.displayLoader({}, true);
      try {
        const { sdr, notes, lineResults, failedNoteCount } = await this.sdrService.submit(this.formState, stagedNotes, undefined, this.sdrAttemptId);
        this.notifySubmitOutcome(lineResults, failedNoteCount);
        this.navigateToDetail(sdr, notes);
      } catch (err) {
        console.error('SDR submit failed', err);
        if (err instanceof SdrAllocationExceededError) {
          this._notifyAllocationExceeded(err);
        } else {
          this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
        }
      } finally {
        await this.uiService.dismissLoader();
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Resolve a previous failed-but-maybe-committed submit before re-posting.
   * Returns true when reconcile handled the outcome and the caller must STOP
   * (either the record already existed → navigated to it, or the server is still
   * unreachable → stay on the form). Returns false only when the record is
   * confirmed absent, so a fresh submit is safe to proceed.
   */
  private async reconcileBeforeSubmit(): Promise<boolean> {
    await this.uiService.displayLoader({}, true);
    try {
      const existing = await this.sdrService.reconcileSubmit(this.sdrAttemptId);
      if (existing) {
        // The earlier submit had actually succeeded — surface it as a normal
        // submission and navigate to the (read-only) detail. Note: any edits made
        // on this form after the failure cannot apply to the committed record.
        this.toast('SDR_SUBMITTED', ToastStyle.INFO);
        this.navigateToDetail(existing, existing.notes ?? []);
        return true;
      }
      return false;
    } catch (err) {
      console.error('SDR reconcile failed', err);
      // Couldn't reach the server to verify — keep the user on the form (the
      // pending id is retained) so they can retry once connectivity is stable.
      // Crucially, do NOT fall through to a blind post that might duplicate.
      this.toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
      return true;
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  /**
   * Surfaces the per-line outcome from Dynamics' PreOperationSRLineItem plugin
   * (OMNI-59706, confirmed contract), plus whether any staged note failed to
   * save. By the time this runs, the header and every accepted line have
   * already committed and a rejected line was simply never created — there's
   * nothing to "remove" from the app, since `sdr.products` (built by
   * createSdr) already excludes rejected lines before this is called; this
   * only tells the rep which product(s) didn't go through and why, using
   * Dynamics' own message text, and — confirmed design — whether a note was
   * silently dropped rather than letting that pass unnoticed.
   */
  private notifySubmitOutcome(lineResults: SdrLineItemResult[], failedNoteCount: number): void {
    const rejected = lineResults.filter((r) => r.status === 'rejected');
    const noteWarning = failedNoteCount > 0
      ? this.translate.instant('SDR_NOTE_SAVE_FAILED', { count: failedNoteCount })
      : '';

    if (!rejected.length) {
      if (!noteWarning) {
        this.toast('SDR_SUBMITTED', ToastStyle.INFO);
        return;
      }
      this.notificationService.notify(
        `${this.translate.instant('SDR_SUBMITTED')} ${noteWarning}`,
        'new-sample-delivery-request',
        'top',
        ToastStyle.DANGER,
      );
      return;
    }

    const details = rejected
      .map((r) => `${r.product.skuName}: ${r.errorMessage || this.translate.instant('SDR_REJECTED_GENERIC_REASON')}`)
      .join('; ');
    const labelKey = rejected.length === lineResults.length
      ? 'SDR_ALL_PRODUCTS_REJECTED'
      : 'SDR_SOME_PRODUCTS_REJECTED';
    const message = `${this.translate.instant(labelKey)} ${details}${noteWarning ? ' ' + noteWarning : ''}`;
    this.notificationService.notify(
      message,
      'new-sample-delivery-request',
      'top',
      ToastStyle.DANGER,
    );
  }

  /**
   * Surfaces revalidateAllocationsBeforeSubmit's fresh pre-submit block —
   * names the specific SKU(s) that no longer fit, mirroring
   * notifySubmitOutcome's convention. Also re-resolves the display chips
   * against the numbers the preflight just fetched, so the rep sees the
   * corrected remaining figure without having to re-open the SKU picker.
   */
  private _notifyAllocationExceeded(err: SdrAllocationExceededError): void {
    const products = err.blockedProducts.map((p) => p.skuName).join(', ');
    this.notificationService.notify(
      this.translate.instant('SDR_ALLOCATION_EXCEEDED_AT_SUBMIT', { products }),
      'new-sample-delivery-request',
      'top',
      ToastStyle.DANGER,
    );
    void this._refreshAllocationsForCurrentContact();
  }

  private navigateToDetail(sdr: SampleDeliveryRequest, notes: IONote[]) {
    this.navService.popChildNavPageWithPageTracking();
    this.navService.pushChildNavPageWithPageTracking(
      PageName.SampleDeliveryRequestDetailComponent,
      PageName.SampleDeliveryRequestDetailComponent,
      this.from ?? PageName.ContactPageComponent,
      { sdr, notes },
    );
  }

  private toast(key: string, style: ToastStyle) {
    this.notificationService.notify(
      this.translate.instant(key),
      'new-sample-delivery-request',
      'top',
      style,
    );
  }

  // Create-form notes are in-memory until the submit batch — the wrapper's
  // noteSaved/noteDeleted events just maintain its internal staged list. The
  // host doesn't need a handler here; persistence happens via getStagedNotes()
  // on submit.

  async onDiscard() {
    const alert = await this.alertCtrl.create({
      message: this.translate.instant('SDR_DISCARD_CONFIRMATION'),
      buttons: [
        { text: this.translate.instant('CANCEL'), role: 'cancel' },
        {
          text: this.translate.instant('DISCARD'),
          handler: () => {
            // Abandoning this attempt — forget its own unconfirmed submit id.
            // (Any record that did commit server-side surfaces on the next
            // timeline refresh.)
            this.sdrService.clearPendingReconcile(this.sdrAttemptId);
            this.navService.popChildNavPageWithPageTracking();
          },
        },
      ],
    });
    await alert.present();
  }
}
